import React, { useState } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  HomeOutlined as HomeIcon,
  HandymanOutlined as HandymanIcon,
  ForumOutlined as ForumIcon,
  EventOutlined as EventIcon,
  StorefrontOutlined as ShopIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useCityInView } from './hooks/city/useHubCity';
import { ToastProvider } from './context/ToastContext';
import Home from './pages/Home';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import ConfirmEmail from './pages/auth/ConfirmEmail';
import Directory from './pages/directory/Directory';
import Feed from './pages/community/Feed';
import PostPage from './pages/community/PostPage';
import Agenda from './pages/community/Agenda';
import Commerces from './pages/directory/Commerces';
import ProviderPage from './pages/directory/ProviderPage';
import MyListing from './pages/pro/MyListing';
import MyOffers from './pages/pro/MyOffers';
import ProviderOnboarding from './pages/pro/ProviderOnboarding';
import MySchedule from './pages/pro/MySchedule';
import MyBookings from './pages/account/MyBookings';
import MyAccount from './pages/account/MyAccount';
import Contact from './pages/info/Contact';
import LegalNotice from './pages/info/LegalNotice';
import TermsOfUse from './pages/info/TermsOfUse';
import MyTasks from './pages/account/MyTasks';
import Search from './pages/Search';
import RequireAccount from './components/auth/RequireAccount';
import Moderation from './pages/moderation/Moderation';
import ProviderOnboardingBanner from './components/pro/ProviderOnboardingBanner';
import { useMyListingSummary } from './hooks/pro/useMyListingSummary';
import ScrollToTop from './components/layout/ScrollToTop';
import { theme } from './theme';
import './styles/App.css';

const ADMIN_URL = process.env.REACT_APP_ADMIN_URL || 'http://localhost:3001';

/** The three public destinations, in the order they are worth visiting. */
const DESTINATIONS = [
  { to: '/services', label: 'Les services', short: 'Services', icon: <HandymanIcon /> },
  { to: '/commerces', label: 'Les commerces', short: 'Commerces', icon: <ShopIcon /> },
  { to: '/quartier', label: 'Le fil du quartier', short: 'Le fil', icon: <ForumIcon /> },
  { to: '/agenda', label: "L'agenda", short: 'Agenda', icon: <EventIcon /> },
];

function isCurrent(pathname, to) {
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * The bar at the top.
 *
 * The API health badge that used to sit here is gone. It was a development
 * instrument on a public page: a resident who reads "API injoignable" learns
 * only that something they cannot act on is broken, and it was the loudest
 * thing in the bar.
 */
/** A heading inside the avatar menu: a label, not something to click. */
const MENU_SECTION_SX = {
  lineHeight: 2.2,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'text.secondary',
  bgcolor: 'transparent',
};

function SiteHeader() {
  const {
    isAuthenticated,
    loading: authLoading,
    getInitials,
    getDisplayName,
    logout,
    user,
    isAdmin,
    isProvider,
    isModerator,
  } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { city: cityInView, cities, choose } = useCityInView();
  const cityName = cityInView?.name || '';
  // A resident's city is their account's, so there is nothing to choose. A
  // visitor with several open has to be able to say which one they came for.
  const visitorPicksCity = !isAuthenticated && cities.length > 1;
  const [searchParams, setSearchParams] = useSearchParams();

  // A neighbourhood in the URL belongs to the city being left, and would
  // empty the list in the next one; a page number is no better kept.
  const chooseCity = (slug) => {
    choose(slug);

    if (searchParams.has('neighborhood') || searchParams.has('page')) {
      const next = new URLSearchParams(searchParams);
      next.delete('neighborhood');
      next.delete('page');
      setSearchParams(next, { replace: true });
    }
  };
  // A shop's own screens are not a service provider's; the listing says which.
  const listingKind = useMyListingSummary()?.kind ?? 'SERVICE';
  const [anchorEl, setAnchorEl] = useState(null);

  const closeMenu = () => setAnchorEl(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    closeMenu();
  };

  return (
    <AppBar position="sticky">
      <Toolbar sx={{ gap: 2, minHeight: { xs: 72, md: 86 }, px: { xs: 2, md: 5.5 } }}>
        {/* The brand and the account block are two columns of equal share,
            so the tabs between them stay put whatever the city's name: with
            auto margins on either side, a longer name pushed them right. */}
        <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', alignItems: 'center' }}>
          <Box
            component={Link}
            to="/"
            sx={{ display: 'flex', alignItems: 'center', gap: 1.5, textDecoration: 'none', minWidth: 0, mr: 2 }}
          >
            {/* The favicon itself, so the tab and the page carry the same mark.
                Empty alt: the name beside it already says what it is. */}
            <Box
              component="img"
              src={`${process.env.PUBLIC_URL}/favicon.svg`}
              alt=""
              sx={{ width: 38, height: 38, flexShrink: 0 }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Typography variant="h5" component="span" sx={{ lineHeight: 1.1 }}>
                Vie&nbsp;de&nbsp;Quartier
              </Typography>
              {/* Their own city when they have said where they live, the one
                  the hub runs in otherwise. It used to be "Bordeaux" in the
                  source, which was true of one deployment and of no other. */}
              <Typography variant="caption" noWrap sx={{ display: { xs: 'none', sm: 'block' } }}>
                {cityName ? `${cityName}, entre voisins` : 'Entre voisins'}
              </Typography>
            </Box>
          </Box>

          {visitorPicksCity && (
            <TextField
              select
              size="small"
              label="Ville"
              value={cityInView?.slug || ''}
              onChange={(event) => chooseCity(event.target.value)}
              sx={{ minWidth: 160, mr: 2, display: { xs: 'none', sm: 'block' } }}
            >
              {cities.map((one) => (
                <MenuItem key={one.slug} value={one.slug}>
                  {one.name}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Box>

        {/* On a phone these three live in the bar at the bottom instead, where
            a thumb reaches them. */}
        <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' }, flexShrink: 0 }}>
          {DESTINATIONS.map((destination) => {
            const current = isCurrent(pathname, destination.to);

            return (
              <Button
                key={destination.to}
                component={Link}
                to={destination.to}
                aria-current={current ? 'page' : undefined}
                sx={{
                  px: 2.25,
                  color: current ? 'secondary.contrastText' : '#564f44',
                  bgcolor: current ? 'secondary.main' : 'transparent',
                  fontWeight: current ? 700 : 500,
                  '&:hover': { bgcolor: current ? 'secondary.dark' : 'action.hover' },
                }}
              >
                {destination.label}
              </Button>
            );
          })}
        </Stack>

        {/* Nothing is shown until the session has actually answered.
            Rendering the signed-out buttons by default meant everybody saw
            "Se connecter / Créer un compte" for a moment and then watched it
            swap for their own avatar — the interface guessing wrong about who
            you are, in the one place that is supposed to know.

            The box keeps its width through all three states, so the tabs to
            its left do not slide when the answer lands. 350px is what the
            signed-out pair actually measures — the wider of the two states;
            on a phone the tabs live in the bottom bar, so nothing sits to the
            left of this block and only a token width is reserved. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flex: '1 1 0',
            gap: { xs: 1, sm: 2 },
            minWidth: { xs: 52, sm: 350 },
            minHeight: 48,
          }}
        >
          {!authLoading && !isAuthenticated && (
            <>
              <Button component={Link} to="/login" variant="text" sx={{ color: '#564f44' }}>
                Se connecter
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="contained"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Créer un compte
              </Button>
            </>
          )}

          {!authLoading && isAuthenticated && (
          <>
            <Box
              onClick={(event) => setAnchorEl(event.currentTarget)}
              role="button"
              tabIndex={0}
              aria-haspopup="menu"
              aria-expanded={Boolean(anchorEl)}
              onKeyDown={(event) => {
                if ('Enter' === event.key || ' ' === event.key) {
                  event.preventDefault();
                  setAnchorEl(event.currentTarget);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 0.5,
                pl: { xs: 0.5, sm: 1.75 },
                borderRadius: 999,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
              title={user?.email || ''}
            >
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: '#564f44', display: { xs: 'none', sm: 'block' } }}
              >
                Mon espace
              </Typography>
              <ExpandMoreIcon sx={{ color: '#564f44', display: { xs: 'none', sm: 'block' } }} />
              <Avatar sx={{ width: 44, height: 44 }}>{getInitials()}</Avatar>
            </Box>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={closeMenu}
              slotProps={{ paper: { sx: { minWidth: 260, mt: 1 } } }}
            >
              <MenuItem disabled sx={{ opacity: '1 !important' }}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                    {getDisplayName()}
                  </Typography>
                  <Typography variant="caption">{user?.email}</Typography>
                </Box>
              </MenuItem>
              <Divider />

              {/* Two spaces under one avatar, and they are not the same
                  person's business: what a resident does with their own
                  account, then what a professional does with their listing.
                  Said with a heading rather than left to a divider, which only
                  says "something changes here" without saying what. */}
              <ListSubheader sx={MENU_SECTION_SX}>Mon espace personnel</ListSubheader>
              <MenuItem onClick={closeMenu} component={Link} to="/mon-compte">
                Mon compte
              </MenuItem>
              <MenuItem onClick={closeMenu} component={Link} to="/mes-rendez-vous">
                Mes rendez-vous
              </MenuItem>
              <MenuItem onClick={closeMenu} component={Link} to="/mes-travaux">
                Mes travaux
              </MenuItem>

              <Divider />
              <ListSubheader sx={MENU_SECTION_SX}>Mon espace professionnel</ListSubheader>

              {/* For somebody who is not one yet, the heading is what explains
                  the invitation underneath it. */}
              {!isProvider && (
                <MenuItem onClick={closeMenu} component={Link} to="/pro/bienvenue">
                  Inscrire mon activité
                </MenuItem>
              )}
              {/* A shop and somebody who comes to you do not run the same
                  business: one announces offers and has no appointments to
                  keep, the other takes requests and has nothing to announce.
                  The API already refuses the wrong one of the two, so a menu
                  entry leading to it is a door onto an error message.

                  On ROLE_PRO alone, and no longer "provider and not
                  admin": roles are a set, so holding ROLE_PRO already
                  means actually offering a service. The extra guard dated from
                  the role hierarchy, where every administrator implicitly held
                  it — and it left an administrator who really does run an
                  activity staring at an empty heading. */}
              {isProvider && [
                  <MenuItem key="listing" onClick={closeMenu} component={Link} to="/pro/ma-fiche">
                    Ma fiche pro
                  </MenuItem>,
                  ...('COMMERCE' === listingKind
                    ? [
                        <MenuItem key="offers" onClick={closeMenu} component={Link} to="/pro/mes-offres">
                          Mes offres
                        </MenuItem>,
                      ]
                    : [
                        <MenuItem key="schedule" onClick={closeMenu} component={Link} to="/pro/agenda">
                          Mon agenda
                        </MenuItem>,
                      ]),
                ]}

              {/* The city moderator's desk: what waits in the cities they
                  hold a mandate for. */}
              {isModerator && [
                <Divider key="moderation-divider" />,
                <MenuItem key="moderation" onClick={closeMenu} component={Link} to="/moderation">
                  Modération
                </MenuItem>,
              ]}

              {isAdmin && [
                <Divider key="admin-divider" />,
                <MenuItem key="admin" onClick={closeMenu} component="a" href={ADMIN_URL}>
                  Espace admin
                </MenuItem>,
              ]}
              <Divider />
              <MenuItem onClick={handleLogout}>Se déconnecter</MenuItem>
            </Menu>
          </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
}

/**
 * The bar at the bottom of a phone.
 *
 * Four destinations, each with its word under the icon: an icon on its own is
 * not a label for somebody who is still learning the interface.
 */
function MobileNav() {
  const { pathname } = useLocation();

  const items = [{ to: '/', label: 'Accueil', icon: <HomeIcon /> }, ...DESTINATIONS];

  return (
    <Box
      component="nav"
      aria-label="Navigation principale"
      sx={{
        display: { xs: 'flex', md: 'none' },
        position: 'sticky',
        bottom: 0,
        zIndex: (t) => t.zIndex.appBar,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      {items.map((item) => {
        const current = '/' === item.to ? '/' === pathname : isCurrent(pathname, item.to);

        return (
          <Box
            key={item.to}
            component={Link}
            to={item.to}
            aria-current={current ? 'page' : undefined}
            sx={{
              flexGrow: 1,
              minHeight: 68,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              textDecoration: 'none',
              color: current ? 'primary.main' : 'text.disabled',
            }}
          >
            {React.cloneElement(item.icon, { sx: { fontSize: 26 } })}
            <Typography
              variant="caption"
              sx={{ color: 'inherit', fontWeight: current ? 700 : 500, fontSize: '0.78125rem' }}
            >
              {item.short || item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function SiteFooter() {
  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 3.25,
      }}
    >
      <Container maxWidth="lg">
        {/* Centred, because there is nothing left on the other side to
            balance against: the city line named one deployment, and
            "Accessibilité" led nowhere — a link that goes nowhere is worse
            than saying nothing on the subject. */}
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'center', gap: 3, flexWrap: 'wrap' }}
        >
          <Typography
            variant="caption"
            component={Link}
            to="/nous-ecrire"
            sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Nous écrire
          </Typography>
          <Typography
            variant="caption"
            component={Link}
            to="/mentions-legales"
            sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Mentions légales
          </Typography>
          <Typography
            variant="caption"
            component={Link}
            to="/conditions-utilisation"
            sx={{ color: 'text.secondary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            Conditions d’utilisation
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

/**
 * `/annuaire/plomberie` kept its category when it became `/services`.
 * `<Navigate>` cannot read a route parameter, so this reads it and forwards.
 */
function CategoryRedirect() {
  const { categorySlug } = useParams();

  return <Navigate to={`/services/${categorySlug}`} replace />;
}

function AppContent() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <ScrollToTop />
      <SiteHeader />
      <ProviderOnboardingBanner />

      <Box component="main" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPassword />} />
          <Route path="/nouveau-mot-de-passe" element={<ResetPassword />} />
          <Route path="/confirmer-email" element={<ConfirmEmail />} />
          <Route path="/services" element={<Directory />} />
          <Route path="/services/:categorySlug" element={<Directory />} />
          <Route path="/commerces" element={<Commerces />} />
          {/* The directory was "l'annuaire" and the shops "les offres" until
              the two errands turned out to be different ones. Old links, and
              anything a resident bookmarked, still land in the right place. */}
          <Route path="/annuaire" element={<Navigate to="/services" replace />} />
          <Route path="/annuaire/:categorySlug" element={<CategoryRedirect />} />
          <Route path="/offres" element={<Navigate to="/commerces" replace />} />
          <Route path="/prestataires/:slug" element={<ProviderPage />} />
          <Route path="/quartier" element={<Feed />} />
          <Route path="/quartier/:uuid" element={<PostPage />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/recherche" element={<Search />} />
          {/* The professional sign-up used to be its own form. It is now a
              checkbox on the one form, so the old address keeps working and
              lands there. */}
          <Route path="/pro/inscription" element={<Navigate to="/register" replace />} />
          <Route
            path="/pro/bienvenue"
            element={
              /* A visitor following "inscrire mon activité" has no account at
                 all, so signing in would be a dead end: they are sent to
                 create one, and come back here. */
              <RequireAccount to="/register">
                <ProviderOnboarding />
              </RequireAccount>
            }
          />
          <Route
            path="/pro/ma-fiche"
            element={
              <RequireAccount>
                <MyListing />
              </RequireAccount>
            }
          />
          <Route
            path="/pro/mes-offres"
            element={
              <RequireAccount>
                <MyOffers />
              </RequireAccount>
            }
          />
          <Route
            path="/pro/agenda"
            element={
              <RequireAccount>
                <MySchedule />
              </RequireAccount>
            }
          />
          <Route path="/nous-ecrire" element={<Contact />} />
          <Route path="/mentions-legales" element={<LegalNotice />} />
          <Route path="/conditions-utilisation" element={<TermsOfUse />} />
          <Route
            path="/moderation"
            element={
              <RequireAccount>
                <Moderation />
              </RequireAccount>
            }
          />
          <Route
            path="/mon-compte"
            element={
              <RequireAccount>
                <MyAccount />
              </RequireAccount>
            }
          />
          <Route
            path="/mes-rendez-vous"
            element={
              <RequireAccount>
                <MyBookings />
              </RequireAccount>
            }
          />
          <Route
            path="/mes-travaux"
            element={
              <RequireAccount>
                <MyTasks />
              </RequireAccount>
            }
          />
        </Routes>
      </Box>

      <SiteFooter />
      <MobileNav />
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
}

export default App;
